import {useConfig} from '@aboutyou/config';
import {InfoIcon} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/info_icon.types';
import {LinkTarget} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/link_target.types';
import {
  SpotlightSection_Media,
  SpotlightSection_SlideElement,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {useHistory} from '@aboutyou/router';
import {
  BannerInfoIcon,
  BannerInfoIconContent,
  BannerMediaData,
  COLORS,
  Chip,
  CountdownChip,
  Flyout,
  GenericIconName,
  Icon,
  ModalDialog,
  RichText,
  SponsoredWithBlurIcon,
  SpotlightCategoryLink,
  SpotlightColorPair,
  SpotlightSlideAmbient,
  SpotlightSlideBaseProps,
  SpotlightSlideCardContent,
  SpotlightSlidePanel,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC, MouseEvent, useCallback, useEffect, useMemo} from 'react';
import {useSponsoredProductPopup} from '../../../backend-services/SponsoredProductPopup/utils/useSponsoredProductPopup';
import {useIsUrgencyTriggersDisabled} from '../../../shared-deprecated/useIsUrgencyTriggersDisabled';
import {useSplitFirstProductIds} from '../../hooks/useSplitFirstProductIds';
import {gradientColorToCss} from '../utils';
import {SlidePropsType} from './types';

const toMedia = (
  media: SpotlightSection_Media,
): BannerMediaData | undefined => {
  const variant = media.variant;
  if (!variant) return undefined;

  if (variant.$case === 'image') {
    return {
      src: {
        kind: 'image',
        url: variant.image.url,
        alt: variant.image.description,
      },
    };
  }

  return {
    src: {
      kind: 'video',
      sources: variant.video.sources,
      lowSources: variant.video.lowSources,
      poster: variant.video.fallbackImage.url,
      alt: variant.video.description,
    },
  };
};

const toCardContent = (
  cardContent: SpotlightSection_SlideElement['cardContent'],
): SpotlightSlideCardContent | undefined => {
  if (!cardContent) return undefined;

  if (cardContent.$case === 'mediaText') {
    const {mediaText} = cardContent;
    return {
      kind: 'text',
      headline: mediaText.headline,
      subline: mediaText.subline || undefined,
      color: mediaText.color,
    };
  }

  return {
    kind: 'logo',
    image: {
      url: cardContent.logo.url,
      description: cardContent.logo.description,
    },
  };
};

const toInfoIconContent = (
  icon: InfoIcon,
  buildCtaClick: (target: LinkTarget) => VoidFunction,
): BannerInfoIconContent | undefined => {
  const variant = icon.variant;
  if (!variant) return undefined;

  if (variant.$case === 'displayText') {
    return {
      type: 'displayText',
      headline: variant.displayText.headline,
      body: <RichText>{variant.displayText.subline.text}</RichText>,
    };
  }

  // No target to open — hide the icon instead of opening an empty sheet (ASFE-14356).
  if (!variant.cta.linkTarget) return undefined;

  return {
    type: 'cta',
    label: variant.cta.label,
    onClick: buildCtaClick(variant.cta.linkTarget),
  };
};

const SpotlightSponsoredButton: FC<{
  label?: string;
  promotionId: string;
  onPauseAutomaticSlide?: VoidFunction;
  onResumeAutomaticSlide?: VoidFunction;
}> = ({label, promotionId, onPauseAutomaticSlide, onResumeAutomaticSlide}) => {
  const {onSponsoredClick, isOpen} = useSponsoredProductPopup();

  const onClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSponsoredClick({e, promotionId});
  };

  useEffect(() => {
    if (isOpen) {
      onPauseAutomaticSlide?.();
    } else {
      onResumeAutomaticSlide?.();
    }
    // pause/resume are recreated every render by useAutomaticSlide and
    // onResume schedules a new autoplay timer per call; this must only fire
    // on isOpen transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <StyledSponsoredWithBlurIcon
      label={label}
      onClick={onClick}
      color="Fill.White"
      iconPosition="left"
    />
  );
};

// The shared Sponsored wrapper centers its content across the full width by
// default; left-align it so it sits flush at the card corner.
const StyledSponsoredWithBlurIcon = styled(SponsoredWithBlurIcon)`
  && {
    justify-content: start;
  }
`;

/**
 * Adapts a backend `SpotlightSection_SlideElement` into the shared Spotlight
 * slide shells. Mirrors the other slide adapters (`ContentSlide`,
 * `HighTierCouponSlide`, …): transform content here, forward slider state via
 * `...rest` so the UI shell owns pagination / animation wiring.
 */
export const SpotlightSlideElement: FC<
  {slide: SpotlightSection_SlideElement} & SlidePropsType
> = ({
  slide,
  isActive,
  isCloseToViewport,
  onSlideClick,
  setNavBorderColor,
  uniqueKey = '',
  'data-testid': dataTestid,
  ...rest
}) => {
  const {device} = useConfig();
  const history = useHistory();
  const urgencyDisabled = useIsUrgencyTriggersDisabled();

  const {linkWithoutFirstProductIds, fullLink} = useSplitFirstProductIds(
    slide.linkTarget.url,
  );

  // Colours are optional: a transparent base lets the ambient glow read
  // through when the backend sends no colour set.
  const backgroundColor = slide.colors?.backgroundColor.hex ?? 'transparent';
  const foregroundColor =
    slide.colors?.foregroundColor.hex ?? COLORS['Text.Black'];

  const media = useMemo(
    () => (slide.media ? toMedia(slide.media) : undefined),
    [slide.media],
  );

  // Card glows mirror the ambient gating in BackgroundCard: colour-only slides
  // echo the ambient blooms on the card; media slides stay glow-free.
  const glowColors = useMemo(() => {
    if (media) return undefined;
    const colors = slide.gradientColors.map(gradientColorToCss);
    return colors.length > 0 ? colors : undefined;
  }, [media, slide.gradientColors]);

  const cardContent = useMemo(
    () => toCardContent(slide.cardContent),
    [slide.cardContent],
  );

  const productImages = useMemo(
    () =>
      slide.productGallery.map(product => ({
        url: product.image.url,
        description: product.image.description,
      })),
    [slide.productGallery],
  );

  const buildCtaClick = useCallback(
    (target: LinkTarget): VoidFunction =>
      () => {
        if (target.external) {
          window.open(target.url, '_blank');
        } else {
          history.push(target.url);
        }
      },
    [history],
  );

  const badge = useMemo(() => {
    const type = slide.badge?.type;
    if (!type) return undefined;

    if (type.$case === 'countdown') {
      return urgencyDisabled ? undefined : (
        <CountdownChip expiryDate={type.countdown.endsAt} />
      );
    }

    return <Chip label={type.label} />;
  }, [slide.badge, urgencyDisabled]);

  const infoIcon = useMemo(() => {
    const icon = slide.infoIcon;
    if (!icon) return undefined;

    const content = toInfoIconContent(icon, buildCtaClick);
    if (!content) return undefined;

    return (
      <BannerInfoIcon
        content={content}
        countdown={
          icon.countdown && !urgencyDisabled
            ? {
                endsAt: icon.countdown.endsAt,
                foregroundColor: icon.countdown.textColor,
              }
            : undefined
        }
        ContentPopperLayoutComponent={
          device === 'mobile' ? Flyout : ModalDialog
        }
        icon={
          <Icon
            asset={GenericIconName.Info}
            width="20px"
            height="20px"
            fill={icon.iconColor || 'currentColor'}
          />
        }
      />
    );
  }, [slide.infoIcon, buildCtaClick, urgencyDisabled, device]);

  const sponsored = slide.sponsored ? (
    <SpotlightSponsoredButton
      label={slide.sponsored.label}
      promotionId={slide.promotionTracker.promotionId}
      onPauseAutomaticSlide={rest.onPauseAutomaticSlide}
      onResumeAutomaticSlide={rest.onResumeAutomaticSlide}
    />
  ) : undefined;

  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setNavBorderColor?.(
      isActive ? slide.colors?.foregroundColor.hex : undefined,
    );
  }, [isActive, slide.colors, setNavBorderColor]);

  const baseProps: SpotlightSlideBaseProps = {
    backgroundColor,
    glowColors,
    media,
    cardContent,
    headline: slide.headline,
    topline: slide.topline,
    foregroundColor,
    productImages,
    badge,
    infoIcon,
    sponsored,
    bannerTarget: {
      url: linkWithoutFirstProductIds || slide.linkTarget.url,
      external: slide.linkTarget.external,
      onClick: () => {
        if (fullLink) {
          history.push(fullLink);
        }
      },
    },
    isActive: Boolean(isActive),
    isCloseToViewport: Boolean(isCloseToViewport),
    onSlideClick,
    uniqueKey,
    'data-testid': dataTestid,
    // Forward slider state so the UI shell owns pagination (same pattern as
    // HighTierCouponSlide → SpotlightSlide).
    onPauseAutomaticSlide: rest.onPauseAutomaticSlide,
    onResumeAutomaticSlide: rest.onResumeAutomaticSlide,
    onStopAutomaticSlide: rest.onStopAutomaticSlide,
    currentSlide: rest.currentSlide,
    maxPages: rest.maxPages,
    scrollToSlide: rest.scrollToSlide,
    isAnimationStopped: rest.isAnimationStopped,
    isAnimationPaused: rest.isAnimationPaused,
  };

  if (device === 'mobile') {
    return <SpotlightSlideAmbient {...baseProps} />;
  }

  const categoryLinks: SpotlightCategoryLink[] = slide.categoryLinks.map(
    link => ({
      label: link.label,
      linkTarget: link.linkTarget,
    }),
  );

  const categoryLinkColors: SpotlightColorPair | undefined =
    slide.categoryLinkColors
      ? {
          foregroundColor: slide.categoryLinkColors.foregroundColor.hex,
          backgroundColor: slide.categoryLinkColors.backgroundColor.hex,
        }
      : undefined;

  return (
    <SpotlightSlidePanel
      {...baseProps}
      categoryLinks={categoryLinks}
      categoryLinkColors={categoryLinkColors}
    />
  );
};
